import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getUrlParamsFetch, handleAxiosError } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants/networking.constants';
import { AlertSeverityEnum, UseMutationsParams } from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';
import { Zone, ZonesPagingRes } from '@/shared/interfaces/erp/mantenimiento-operaciones';

const { get, post, put, remove } = erpApi();

///* tanSctack query
export const useFetchZones = (params?: getZonesParams, enabled = true) => {
  return useQuery({
    queryKey: ['zones', ...Object.values(params ?? {})],
    queryFn: async () => {
      return getZones(params);
    },
    enabled,
  });
};

export const useGetZone = (id: string) => {
  return useQuery({
    queryKey: ['zones', id],
    queryFn: async () => {
      return getZone(id);
    },
  });
};

export const useCreateZone = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createZone,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['zones'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Zona creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateZone = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateZone,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['zones'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Zona actualizada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useRemoveZone = () => {
  const queryClient = useQueryClient();
  const setConfirmDialogIsOpen = useUiStore.getState().setConfirmDialogIsOpen;

  return useMutation({
    mutationFn: removeZone,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['zones'] });
    },
    onSettled: () => {
      setConfirmDialogIsOpen(false);
    },
  });
};

///* axios
export type getZonesParams = Partial<Zone> & {
  page?: number;
  page_size?: number;

  filterByState?: boolean;
};

export type CreateZoneParams = Omit<Zone, 'id_zona'>;
export type UpdateZoneParams = {
  id: string;
  data: CreateZoneParams;
};

export const getZones = async (params?: getZonesParams) => {

  const queryParams = getUrlParamsFetch(params || {});

  return get<ZonesPagingRes>(`${urlBackend}api/v1/zona/?${queryParams}`, true);
};

export const getZone = async (id: string) => {
  try {
    return await get<Zone>(`${urlBackend}api/v1/zona/${id}/`, true);
  } catch (error) {
    handleAxiosError(error);
  }
};

export const createZone = async (data: CreateZoneParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<Zone>(`${urlBackend}api/v1/zona/`, data, true);
};

export const updateZone = async ({ id, data }: UpdateZoneParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<Zone>(`${urlBackend}api/v1/zona/${id}/`, data, true);
};

export const removeZone = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await remove<Zone>(`${urlBackend}api/v1/zona/${id}/`, true);

  setAlert(true, res.message, res.status);
  return res;
};